import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { useSnackbar } from "notistack";
import Form from "../Forms/Form";
import { FormField } from "../Forms/FormField";
import { FieldTypes } from "../Forms/FieldTypes";
import * as Yup from "yup";
import axios from "axios";
import AuthPageFrame, { AUTH_LINK_COLOR } from "./AuthPageFrame";
import { authErrorMessage } from "./authErrorMessage";

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const api = useApi(false); // Don't include auth header for forgot password
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);

    try {
      const response = await api.post(
        "/auth/forgot-password",
        { username: values.email },
        // Same reasoning as the reset screen (see ResetPassword.tsx): this screen is the sole
        // reporter, and the bare 429 from the "forgot-password" rate limiter has no body to carry
        // the server-side flag.
        { suppressErrorDialog: true },
      );

      if (response.data?.success) {
        setEmailSent(true);
        // Navigate to reset password page with email
        navigate(
          `/reset-password?email=${encodeURIComponent(String(values.email))}`,
        );
        return;
      }

      // NEW-26 note: an address that names nobody now takes the branch ABOVE, not this one. The
      // API used to 500 on Cognito's UserNotFoundException while a known address got a 200, so this
      // screen's error message was itself the account-existence oracle its careful "if an account
      // exists…" copy was written to prevent. What is left here is real trouble: our own throttling,
      // or a Cognito the API could not reach.
      enqueueSnackbar(
        authErrorMessage(
          response.status,
          response.data,
          "Failed to process request. Please try again.",
        ),
        { variant: "error" },
      );
    } catch (error) {
      const response = axios.isAxiosError(error) ? error.response : undefined;
      enqueueSnackbar(
        authErrorMessage(
          response?.status ?? 0,
          response?.data,
          "Failed to process request. Please try again.",
        ),
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const fields: FormField[] = [
    {
      id: "email",
      email: true,
      text: "Email Address",
      fixedLabel: true,
      type: FieldTypes.Text,
      columns: 12,
      yupSchema: Yup.string()
        .email("Invalid email address")
        .required("Email is required")
        .max(254, "Email address is too long"),
    },
  ];

  return (
    <AuthPageFrame eyebrow="Account help" title="Reset your password">
      {!emailSent ? (
        <>
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
          >
            Enter your email address and we'll send you a code to reset your
            password.
          </Typography>

          <Form
            fields={fields}
            onSubmit={handleSubmit}
            submitButtonText={loading ? "Sending..." : "Send reset code"}
            fieldVariant="outlined"
            centerActionButtons
          />

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <RouterLink to="/login" style={{ textDecoration: "none" }}>
              <Typography
                variant="body2"
                sx={{
                  color: AUTH_LINK_COLOR,
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Back to sign in
              </Typography>
            </RouterLink>
          </Box>
        </>
      ) : (
        <Typography
          variant="body1"
          sx={{ textAlign: "center", color: "text.secondary" }}
        >
          If an account exists with this email, you will receive a code shortly.
          Please check your email and follow the instructions to reset your
          password.
        </Typography>
      )}
    </AuthPageFrame>
  );
};

export default ForgotPassword;
